import { lazy } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext.jsx'
import { usePlatform } from './contexts/PlatformContext.jsx'
import Layout from './components/Layout.jsx'
import Login from './pages/Login.jsx'
import {
  dashboardChunk,
  projectChunk,
  settingsChunk,
  tokenGroupChunk,
  logsChunk,
  knowledgeBaseChunk,
  warmupChunk,
  exchangeDistributorChunk,
  exchangeDistributorSettingsChunk,
  cexDashboardChunk,
  cexTokensChunk,
  cexTokenGroupChunk,
  cexExchangesChunk,
  cexAlgorithmsChunk,
  cexBotsChunk,
  cexTerminalChunk,
} from './pages/lazyChunks.js'

const Dashboard = lazy(dashboardChunk)
const Project = lazy(projectChunk)
const Settings = lazy(settingsChunk)
const TokenGroup = lazy(tokenGroupChunk)
const Logs = lazy(logsChunk)
const KnowledgeBase = lazy(knowledgeBaseChunk)
const Warmup = lazy(warmupChunk)
const ExchangeDistributor = lazy(exchangeDistributorChunk)
const ExchangeDistributorSettings = lazy(exchangeDistributorSettingsChunk)

const CexDashboard = lazy(cexDashboardChunk)
const CexProject = lazy(cexTokensChunk)
const CexTokenGroup = lazy(cexTokenGroupChunk)
const CexExchanges = lazy(cexExchangesChunk)
const CexAlgorithms = lazy(cexAlgorithmsChunk)
const CexBots = lazy(cexBotsChunk)
const CexTerminal = lazy(cexTerminalChunk)

/**
 * Toggle-aware dispatchers: the same URL renders DEX or CEX content depending
 * on `activePlatform` from PlatformContext. This keeps the sidebar identical
 * on both subdomains — only the clients view swaps with the slider.
 */
function DashboardDispatcher() {
  const { activePlatform } = usePlatform()
  return activePlatform === 'cex' ? <CexDashboard /> : <Dashboard />
}
function ProjectDispatcher() {
  const { activePlatform } = usePlatform()
  return activePlatform === 'cex' ? <CexProject /> : <Project />
}
function TokenGroupRoute() {
  const { groupId } = useParams()
  const { activePlatform } = usePlatform()
  if (activePlatform === 'cex') return <CexTokenGroup key={groupId} />
  return <TokenGroup key={groupId} />
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, authInitialized } = useAuth()
  const location = useLocation()
  if (!authInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/95">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      </div>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return children
}

/** Старые ссылки /cex/tokens → единый маршрут /project (CEX-редизайн). */
function CexTokensLegacyToProject() {
  return <Navigate to="/project" replace />
}
function CexTokensLegacyToGroup() {
  const { groupId } = useParams()
  return <Navigate to={`/project/group/${groupId}`} replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardDispatcher />} />
        <Route path="project" element={<ProjectDispatcher />} />
        <Route path="project/group/:groupId" element={<TokenGroupRoute />} />
        <Route
          path="project/group/:groupId/terminal/:exchange/:symbol"
          element={<CexTerminal />}
        />
        <Route path="knowledge" element={<KnowledgeBase />} />
        <Route path="logs" element={<Logs />} />
        <Route
          path="warmup/exchange-distributor/settings"
          element={<ExchangeDistributorSettings />}
        />
        <Route path="warmup/exchange-distributor" element={<ExchangeDistributor />} />
        <Route path="warmup" element={<Warmup />} />
        <Route path="settings" element={<Settings />} />
        <Route path="account" element={<Navigate to="/settings" replace />} />

        {/* Deep-link routes for CEX-only management surfaces. Not in the
            sidebar (everything shared by default). Reachable via direct URL
            or from within a CEX client detail view. */}
        <Route path="cex/tokens" element={<CexTokensLegacyToProject />} />
        <Route path="cex/tokens/:groupId" element={<CexTokensLegacyToGroup />} />
        <Route path="cex/exchanges" element={<CexExchanges />} />
        <Route path="cex/algorithms" element={<CexAlgorithms />} />
        <Route path="cex/bots" element={<CexBots />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
